import { type InputHTMLAttributes, forwardRef } from 'react';
import { cn } from '@/src/shared/lib/utils';

interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
}

const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, disabled, id, checked, ...props }, ref) => {
    const checkboxId = id || label?.toLowerCase().replace(/\s+/g, '-');

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2.5">
          <div className="relative h-6 w-6 shrink-0">
            <input
              ref={ref}
              type="checkbox"
              id={checkboxId}
              checked={checked}
              className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
              disabled={disabled}
              aria-invalid={!!error}
              aria-describedby={error ? `${checkboxId}-error` : undefined}
              {...props}
            />
            <div
              className={cn(
                'pointer-events-none h-6 w-6 rounded-[10px] border-2 transition-all duration-200',
                'flex items-center justify-center',
                // Unchecked state
                'border-accent-primary bg-background',
                // Hover
                'peer-hover:bg-accent-primary/5',
                // Focus ring
                'peer-focus:ring-2 peer-focus:ring-accent-primary peer-focus:ring-offset-2',
                // Checked state
                checked
                  ? 'border-accent-primary bg-accent-primary'
                  : '',
                // Disabled state
                disabled && !checked && 'border-border-primary bg-surface-sunken opacity-50',
                disabled && checked && 'border-accent-disabled bg-accent-disabled opacity-50',
                // Error state
                error && !checked && 'border-feedback-error',
                className
              )}
            >
              {checked && (
                <svg
                  width="14"
                  height="11"
                  viewBox="0 0 14 11"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.5 5L5.5 9L12.5 1.5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </div>
          </div>
          {label && (
            <label
              htmlFor={checkboxId}
              className={cn(
                'text-mobile-body md:text-tablet-body lg:text-desktop-body cursor-pointer select-none',
                disabled ? 'text-text-disabled' : 'text-text-primary'
              )}
            >
              {label}
            </label>
          )}
        </div>
        {error && (
          <p
            id={`${checkboxId}-error`}
            className="text-mobile-body-sm md:text-tablet-body-sm lg:text-desktop-body-sm text-feedback-error ml-8"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export { Checkbox };
export type { CheckboxProps };
